import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { styled } from '@mui/material/styles';

// Redux
import {
  setCsatDetailVisibility,
  setCsatType,
  setCsatVisibility,
} from 'actions/workflows/workflows';
import {
  selectCsatConfig,
  selectCsatFormData,
  selectIsCsatTextEmptyFromResponse,
  selectTranslatedCsatText,
} from 'reducers/workflows';
import { selectLegibleElementColor } from 'reducers/config';

// Helpers
import { CSAT_OPTIONS } from './utils';

import CustomerSatisfactionOption from './CustomerSatisfactionOption';

import { useTheme } from '@mui/material/styles';
import { CsatFormData } from 'api/types';

const CustomerSatisfactionCard: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isCsatVisible } = useAppSelector(selectCsatConfig);
  const { csatScore } = useAppSelector(selectCsatFormData);
  const csatConfig = useAppSelector(selectCsatConfig);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const isCsatSurveyFinished = Boolean(csatConfig?.isCsatSurveyFinished);
  const translatedCsatText = useAppSelector(selectTranslatedCsatText);
  const isCsatTextEmptyFromResponse = useAppSelector(
    selectIsCsatTextEmptyFromResponse,
  );

  const isCard = !isCsatVisible;

  if (isCsatTextEmptyFromResponse) return;

  if (isCsatSurveyFinished && csatScore && isCard) {
    const labelKey = CSAT_OPTIONS[csatScore];
    const label = translatedCsatText?.text[labelKey] || '';
    const message =
      translatedCsatText?.text.CSAT_YOU_RATED?.replace('%s', label) || '';

    return (
      <EditContainer>
        <EditMessage color={theme.palette.colors.grey[600]}>
          {message}
        </EditMessage>
        <EditSurveyButton
          color={legibleElementColor}
          onClick={() => {
            dispatch(setCsatType('inline'));
            dispatch(setCsatDetailVisibility(true));
            dispatch(setCsatVisibility(true));
          }}
        >
          {translatedCsatText?.text.CSAT_CHANGE_RATING}
        </EditSurveyButton>
      </EditContainer>
    );
  }

  return (
    <Container
      backgroundColor={theme.palette.colors.white}
      isCard={isCard}
      title={translatedCsatText?.text.CSAT_RATING_QUESTION}
    >
      <Message color={theme.palette.text.primary} data-testid='csat-message'>
        {translatedCsatText?.text.CSAT_RATING_QUESTION}
      </Message>
      <OptionsContainer>
        {Object.entries(CSAT_OPTIONS).map(([score, label]) => {
          return (
            <CustomerSatisfactionOption
              key={label}
              label={label}
              selectedScore={Number(score) as CsatFormData['csatScore']}
            />
          );
        })}
      </OptionsContainer>
    </Container>
  );
};

export default CustomerSatisfactionCard;

const EditContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const EditMessage = styled('p')<{
  color: string;
}>`
  color: ${props => props.color};
  text-align: center;
  margin: 0px;
`;

const EditSurveyButton = styled('button')<{
  color: string;
}>`
  cursor: pointer;
  color: ${props => props.color};
  border: none;
  background-color: transparent;
  font-weight: 500;
  outline: none;

  :focus-within {
    outline: 2px solid var(--legible-element-color);
  }
`;

const Container = styled('form')<{
  backgroundColor: string;
  isCard?: boolean;
}>`
  border-width: ${props => (props.isCard ? 1 : 0)}px;
  border-style: ${props => (props.isCard ? 'solid' : 'none')};
  border-color: ${props => (props.isCard ? 'rgba(0, 0, 0, 0.1)' : 'unset')};
  background-color: ${props => props.backgroundColor};
  box-shadow: ${props =>
    props.isCard ? '0px 4px 12px rgba(0, 0, 0, 0.05)' : 'unset'};
  padding: ${props => (props.isCard ? '20px 16px 32px' : 'unset')};
  border-radius: ${props => (props.isCard ? '9' : '0')}px;
  display: flex;
  flex-direction: column;
  margin-bottom: 0px;
`;

const Message = styled('p')<{ color: string }>`
  color: ${props => props.color};
  padding: 0 10px;
  margin: 0;
  font-size: 18px;
  font-style: normal;
  line-height: 26px;
  text-align: center;
  &.fade-enter {
    opacity: 0;
  }
  &.fade-enter-active {
    opacity: 1;
    transition: opacity 1s;
  }
`;

const OptionsContainer = styled('div')`
  padding: 32px 0px 8px 0px;
  display: flex;
  height: 64px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  text-align: center;
`;
